import * as React from 'react';
import { BuyerCustomerDTO, DiscountMode, PriceListDTO, Res, SoldProductType, TicketDTO, TicketLineDTO } from 'core/api';
import * as UI from 'core/ui';
import { Cols, Col, Flex } from 'core/ui';

function getLineCashDiscountCode(line: TicketLineDTO): string {
	return line.AppliedDiscountMode === DiscountMode.Cash ? line.AppliedDiscountCode : '';
}

function getLineDiscountPercentageCode(line: TicketLineDTO): string {
	const codes: string[] = [];

	if (line.AppliedOfferCode)
		codes.push(line.AppliedOfferCode);

	if (line.AppliedDiscountMode === DiscountMode.Rate)
		codes.push(line.AppliedDiscountCode);

	return codes.join('/');
}

const LineContent = React.forwardRef<HTMLDivElement, { line: TicketLineDTO, selected: boolean, showLineNotes: boolean } & React.HTMLProps<HTMLDivElement>>(function LineContent(props, ref) {

	const { line, selected, showLineNotes, ...rest } = props;
	const selectedStyle = selected
		? { backgroundColor: '#E6E6E6', borderRadius: '5px' }
		: undefined;

	const lineContent = line.Type === SoldProductType.Standard || line.Type === SoldProductType.MenuHeader
		? <Cols style={selectedStyle}>
			<Col width='5ex' style={{ fontSize: 'x-large', color: 'black' }} className='font-bold text-right m-r-xl'>{line.Quantity}</Col>
			<Col width='*' style={{ fontSize: 'x-large', color: 'black' }} className='font-bold '>
				<div>{line.Product.SaleFormatName}</div>
				{line.Product.Size.Id > 0 ? <div className='m-l'>{`${line.Product.Size.Name}/${line.Product.Color.Name}`}</div> : null}
			</Col>
			<Col width='7ex' style={{ fontSize: 'x-large' }} className='text-right'>{UI.Format.asPrice(line.UnitPrice)}</Col>
			<Col width='8ex' style={{ fontSize: 'x-large', color: 'black' }} className='font-bold  text-right'>{UI.Format.asPrice(line.TotalAmount)}</Col>
		</Cols>
		: null;

	const addinsContent = line.Addins.length !== 0
		? line.Addins.map((addin, index) => <Cols key={index}>
			<Col width='5ex' style={{ fontSize: 'x-large' }} className='text-right m-r-xl' ></Col>
			{line.Type === SoldProductType.MenuItem ? <Col width='2ex' style={{ fontSize: 'x-large' }}></Col> : null}
			<Col width='*' style={{ fontSize: 'x-large' }} className='m-l' >{Res.Web.SCREEN_DISPLAY_TICKET_LINES_CONTROL_PRODUCT_WITH_LBL(addin.SaleFormatName)}</Col>
		</Cols>)
		: null;

	const menuLinesContent = line.Type === SoldProductType.MenuItem
		? <Cols style={selectedStyle}>
			<Col width='5ex' style={{ fontSize: 'x-large' }} className='text-right m-r-xl'>{line.Quantity}</Col>
			<Col width='*' style={{ fontSize: 'x-large' }}>{line.Product.SaleFormatName}</Col>
			<Col width='7ex' style={{ fontSize: 'x-large' }}></Col>
			<Col width='7ex' style={{ fontSize: 'x-large' }} className='text-right'>{line.TotalAmount > 0 ? `+${UI.Format.asPrice(line.TotalAmount)}` : '(M)'}</Col>
		</Cols>
		: null;

	const removedContent = line.RemovedIngredients.length !== 0
		? line.RemovedIngredients.map((removed, index) => <Cols key={index}>
			<Col width='5ex' style={{ fontSize: 'x-large' }} className='text-right m-r-xl'></Col>
			{line.Type === SoldProductType.MenuItem ? <Col width='2ex' style={{ fontSize: 'x-large' }}></Col> : null}
			<Col width='*' style={{ fontSize: 'x-large' }} className='m-l'>{Res.Web.SCREEN_DISPLAY_TICKET_LINES_CONTROL_PRODUCT_WITHOUT_LBL(removed.SaleFormatName)}</Col>
		</Cols>)
		: null;


	const notes = line.Notes.length !== 0 && showLineNotes
		? line.Notes.map((note, index) => <Cols key={index}>
			<Col width='5ex' style={{ fontSize: 'x-large' }} className='text-right m-r-xl' ></Col>
			<Col width='*' className='text-muted' style={{ fontSize: 'x-large' }}>*  {note}  *</Col>
		</Cols>)
		: null;

	const cashDiscount = line.HasCashDiscount
		? <Cols>
			<Col width='5ex' style={{ fontSize: 'x-large' }} className='text-right m-r-xl' ></Col>
			<Col width='*' className='text-muted' style={{ fontSize: 'x-large' }}>
				{Res.Web.SCREEN_DISPLAY_DISCOUNT_LBL()} {getLineCashDiscountCode(line)}: {UI.Format.asMoney(line.CashDiscount)}
			</Col>
		</Cols>
		: null;

	const percentageDiscount = line.HasDiscountPercentage
		? <Cols>
			<Col width='5ex' style={{ fontSize: 'x-large' }} className='text-right m-r-xl' ></Col>
			<Col width='*' className='text-muted' style={{ fontSize: 'x-large' }}>
				{Res.Web.SCREEN_DISPLAY_DISCOUNT_LBL()} {getLineDiscountPercentageCode(line)}: {line.DiscountPercentage}% ({UI.Format.asMoney(line.DiscountPercentageInCash)})
			</Col>
		</Cols>
		: null;

	return <div ref={ref} {...rest}>
		{lineContent}
		{menuLinesContent}
		{addinsContent}
		{removedContent}
		{notes}
		{percentageDiscount}
		{cashDiscount}
	</div>;
});

function CustomerControl({ customer, priceList }: { customer: BuyerCustomerDTO; priceList: PriceListDTO; }): React.ReactNode | null {
	if (customer.Id === 0)
		return null;

	return <Cols className='font-bold m-l-xs' style={{ fontSize: 'x-large', color: 'black' }}>
		<Col>{Res.Web.SCREEN_DISPLAY_CUSTOMER_FOOTER_CONTROL_CUSTOMER_LBL()}</Col>
		<Col width='*'>{customer.FiscalName} ({priceList.Name})</Col>
	</Cols>;
}

function DiscountContent({ ticket }: { ticket: TicketDTO; }): React.ReactNode | null {
	if (ticket.DiscountPercentage === 0 && ticket.CashDiscount === 0)
		return null;

	return <Cols className='font-bold m-l-xs m-r-xs' style={{ fontSize: 'x-large', color: 'black' }}>
		<Col width='*' className='text-left'> {Res.Web.SCREEN_DISPLAY_DISCOUNTS_TICKET_FOOTER_CONTROL_DISCOUNT_PERCENTAGE_LBL()}  {UI.Format.asPercentage(ticket.DiscountPercentage)} </Col>
		<Col width='*' className='text-center'> ({UI.Format.asMoney(ticket.DiscountPercentageInCash)}) </Col>
		<Col width='*' className='text-right'> {Res.Web.SCREEN_DISPLAY_DISCOUNTS_TICKET_FOOTER_CONTROL_CASH_DISCOUNT_LBL()} {UI.Format.asMoney(ticket.CashDiscount)} </Col>
	</Cols>;
}

function LinesControl({ ticket, selectedLine, showLineNotes }: { ticket: TicketDTO; selectedLine?: TicketLineDTO; showLineNotes: boolean }): React.ReactNode {

	const ref = React.createRef<HTMLDivElement>();
	const selectedLineCandidate = selectedLine || ticket.Lines.last();

	React.useEffect(() => {
		ref.current?.scrollIntoView({
			behavior: 'smooth',
			block: 'start',
		});
	});

	return <>{
		ticket.Lines.map(line =>
			<LineContent ref={selectedLineCandidate?.Id === line.Id ? ref : undefined} key={line.Id} line={line} selected={selectedLineCandidate?.Id === line.Id} showLineNotes={showLineNotes} />
		)}
	</>;
}


export default function TicketControl({ ticket, selectedLine, showLineNotes }: { ticket: TicketDTO; selectedLine?: TicketLineDTO; showLineNotes: boolean }): React.ReactNode {
	return <Flex.Vertical>
		<Flex.Item size='*' className='p-xs' style={{ overflow: 'hidden' }}>
			<LinesControl ticket={ticket} selectedLine={selectedLine} showLineNotes={showLineNotes} />
		</Flex.Item>
		<Flex.Item>
			<CustomerControl customer={ticket.Customer} priceList={ticket.PriceList} />
		</Flex.Item>
		<Flex.Item className='m-b'>
			<DiscountContent ticket={ticket} />
		</Flex.Item>
		<Flex.Item style={{ backgroundColor: '#000000' }} size='60px'>
			<h1 className='text-center font-bold' style={{ color: '#FFFFFF' }}>
				{Res.Web.SCREEN_DISPLAY_TICKET_CONTROL_TOTAL_LBL()} {UI.Format.asMoney(ticket.PendingAmount)}
			</h1>
		</Flex.Item>
	</Flex.Vertical>;
}
